import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAtividades } from '../../hooks/useAtividades.js';
import { useLeads } from '../../hooks/useLeads.js';
import { AtividadesList } from './AtividadesList.js';
import { AtividadeForm } from './AtividadeForm.js';
import type { Lead } from '@azoupvendas/shared';

// AIDEV-NOTE: Dialog de detalhe do Lead — exibe dados resumidos + ultimas 3 atividades
// Permite criar nova atividade sem sair do contexto e navegar para pagina completa de atividades
// AIDEV-NOTE: Componente dividido em outer (guarda null) e inner (hooks) para evitar hook condicional

interface LeadDetailDialogProps {
  lead: Lead | null;
  open: boolean;
  onClose: () => void;
  onEdit: (lead: Lead) => void;
}

const ETAPA_COLOR: Record<string, string> = {
  novo: '#1976d2',
  qualificado: '#ed6c02',
  proposta: '#9c27b0',
  ganho: '#2e7d32',
  perdido: '#d32f2f'
};

export function LeadDetailDialog({ lead, open, onClose, onEdit }: LeadDetailDialogProps) {
  if (!lead) return null;

  return <LeadDetailDialogContent lead={lead} open={open} onClose={onClose} onEdit={onEdit} />;
}

// AIDEV-NOTE: Componente interno recebe lead nao-nulo, permitindo que hooks sejam chamados incondicionalmente
function LeadDetailDialogContent({
  lead,
  open,
  onClose,
  onEdit
}: {
  lead: Lead;
  open: boolean;
  onClose: () => void;
  onEdit: (lead: Lead) => void;
}) {
  const navigate = useNavigate();
  const [atividadeFormOpen, setAtividadeFormOpen] = useState(false);
  const { atividades, isLoading, isError, createAtividade, completeAtividade } = useAtividades({
    leadId: lead.id
  });
  const { updateEtapa } = useLeads();

  // AIDEV-NOTE: Marcar lead como ganho diretamente do dialog de detalhe
  const handleMarcarGanho = async () => {
    await updateEtapa.mutateAsync({ id: lead.id, etapa: 'ganho' });
    onClose();
  };

  const recentes = [...atividades].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">{lead.nome}</Typography>
              <Typography variant="caption" color="text.secondary">
                Criado em {new Date(lead.created_at).toLocaleDateString('pt-BR')}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Dados do lead */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={1} mb={2}>
                <Grid item xs={6}>
                  <Box bgcolor="grey.50" borderRadius={1} p={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Valor
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {lead.valor_estimado != null
                        ? new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(lead.valor_estimado)
                        : '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box bgcolor="grey.50" borderRadius={1} p={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Etapa
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ color: ETAPA_COLOR[lead.estagio] }}
                    >
                      {lead.estagio.charAt(0).toUpperCase() + lead.estagio.slice(1)}
                    </Typography>
                  </Box>
                </Grid>
                {lead.probabilidade != null && (
                  <Grid item xs={6}>
                    <Box bgcolor="grey.50" borderRadius={1} p={1.5}>
                      <Typography variant="caption" color="text.secondary">
                        Probabilidade
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {lead.probabilidade}%
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {lead.proximo_contato_em && (
                  <Grid item xs={6}>
                    <Box bgcolor="grey.50" borderRadius={1} p={1.5}>
                      <Typography variant="caption" color="text.secondary">
                        Prox. contato
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {new Date(lead.proximo_contato_em).toLocaleDateString('pt-BR')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Atividades */}
            <Grid item xs={12} md={5}>
              <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
              <Box pl={{ md: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Atividades</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAtividadeFormOpen(true)}
                  >
                    + Nova
                  </Button>
                </Box>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={20} />
                  </Box>
                ) : isError ? (
                  <Typography color="error" variant="body2">
                    Erro ao carregar atividades
                  </Typography>
                ) : (
                  <AtividadesList
                    atividades={recentes}
                    onComplete={(id) => completeAtividade.mutate(id)}
                    maxItems={3}
                    onVerTodas={() => {
                      onClose();
                      navigate('/atividades');
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onEdit(lead)}>Editar Lead</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarcarGanho}
            disabled={lead.estagio === 'ganho'}
          >
            Marcar Ganho
          </Button>
        </DialogActions>
      </Dialog>

      <AtividadeForm
        open={atividadeFormOpen}
        onClose={() => setAtividadeFormOpen(false)}
        onSubmit={async (data) => {
          await createAtividade.mutateAsync(data);
        }}
        leadId={lead.id}
      />
    </>
  );
}
